import { useState, useEffect } from 'react';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';
import { useAuth } from 'auth/AuthContext';
import ButtonComponent from 'components/common/ButtonComponent';
import SectionIntro from 'components/common/SectionIntro';
import TableComponent from 'components/common/TableComponent';
import GenerateReportModal from 'components/modals/GenerateReportModal';

const Reports = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState({
    reports: [],
    loading: true,
    isGenerateModalOpen: false
  });

  const fetchReports = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/reports/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });
      setState(s => ({
        ...s,
        reports: Array.isArray(response.data) ? response.data : [],
        loading: false
      }));
    } catch (err) {
      showAlert({ message: t('reports.loadFailed'), icon: 'error' });
      setState(s => ({ ...s, loading: false }));
    }
  };

  const handleDownloadReport = async (reportId, fileName) => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/reports/download/${reportId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        responseType: 'blob'
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `report_${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showAlert({ message: t('reports.downloadSuccess'), icon: 'success' });
    } catch (err) {
      showAlert({ message: t('reports.downloadFailed'), icon: 'error' });
    }
  };

  const formatFileSize = bytes => {
    if (!bytes) return t('common.notAvailable');
    const kb = bytes / 1024;
    const mb = kb / 1024;
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
  };

  const formatDate = dateString => {
    if (!dateString) return t('common.notAvailable');
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    if (user?.farmId) {
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.farmId]);

  if (state.loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">{t('reports.loading')}</div>
      </div>
    );
  }

  const tableData = {
    columns: [
      {
        key: 'reportName',
        label: t('table.columns.reportName'),
        cell: value => (
          <div className="flex items-center">
            <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-3" />
            <span className="text-sm font-medium text-gray-900">{value}</span>
          </div>
        )
      },
      {
        key: 'reportType',
        label: t('table.columns.type'),
        cell: value => (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
            {value}
          </span>
        )
      },
      {
        key: 'fileSize',
        label: t('table.columns.fileSize'),
        cell: value => formatFileSize(value)
      },
      {
        key: 'createdAt',
        label: t('table.columns.createdAt'),
        cell: value => (
          <div className="flex items-center">
            <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
            <span>{formatDate(value)}</span>
          </div>
        )
      },
      {
        key: 'description',
        label: t('table.columns.description'),
        cell: value => (
          <span className="max-w-xs truncate block">{value || t('reports.noDescription')}</span>
        )
      },
      {
        key: 'actions',
        label: t('common.actions'),
        cell: (value, row) => (
          <button
            onClick={() => handleDownloadReport(row.reportId, row.fileName)}
            className="inline-flex items-center gap-2 rounded-md border border-sky-400/20 bg-sky-400/10 px-3 py-2 font-command-label text-[11px] uppercase tracking-[0.16em] text-sky-300 transition-colors hover:bg-sky-400/20"
            title={t('reports.downloadReport')}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {t('common.download')}
          </button>
        )
      }
    ],
    rows: state.reports
  };

  return (
    <div className="p-6">
      <SectionIntro
        kicker={t('pageIntro.managementKicker')}
        title={t('reports.title')}
        description={t('pageIntro.reportsDescription')}
        actions={
          <ButtonComponent onClick={() => setState(s => ({ ...s, isGenerateModalOpen: true }))}>
            <PlusIcon className="w-5 h-5" />
            <span className="font-medium">{t('reports.generateReport')}</span>
          </ButtonComponent>
        }
        className="mb-6"
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      {/* Reports Table */}
      {state.reports.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-12">
          <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">{t('reports.noReportsFound')}</p>
          <p className="text-gray-400 text-sm mt-2">{t('reports.getStarted')}</p>
        </div>
      ) : (
        <TableComponent tableData={tableData} defaultSortColumn="createdAt" />
      )}

      {/* Generate Report Modal */}
      <GenerateReportModal
        isOpen={state.isGenerateModalOpen}
        onClose={() => setState(s => ({ ...s, isGenerateModalOpen: false }))}
        onGenerate={fetchReports}
      />
    </div>
  );
};

export default Reports;
